import styled from 'styled-components'

// Shared dropdown container with opaque background.
// `$placement` controls whether the menu renders below (default) or above the
// input.
export const TypeaheadDropdownContainer = styled.div<{
  $minWidth?: string
  $maxWidth?: string
  $placement?: 'top' | 'bottom'
}>`
  background: ${({ theme }) => theme.forecast.cardBackground};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.3);
  min-width: ${({ $minWidth = '16rem' }) => $minWidth};
  max-width: ${({ $maxWidth = '20rem' }) => $maxWidth};
  max-height: 25rem;
  z-index: 10000;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ${({ $placement = 'bottom' }) =>
    $placement === 'top'
      ? `
        position: absolute;
        left: 0;
        bottom: calc(100% + 2rem);
      `
      : `
        position: relative;
      `}
`

// Shared dropdown header
export const TypeaheadDropdownHeader = styled.div`
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`

// Shared scrollable area for dropdown content
export const TypeaheadScrollArea = styled.div`
  flex: 1;
  max-height: 20rem; /* Leave room for header and filters */
  overflow-y: auto;

  /* Keep scroll contained so reaching the top/bottom of the list doesn't
     chain the scroll to the surface behind the dropdown. */
  overscroll-behavior: contain;

  /* Ensure proper background coverage */
  background: ${({ theme }) => theme.forecast.cardBackground};

  /* Custom scrollbar styling for webkit browsers */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.forecast.border} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.forecast.border};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.forecast.secondaryText};
    }
  }
`

// Shared option base styles
export const TypeaheadOption = styled.button<{ $isSelected: boolean }>`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.forecast.primary + '20' : 'transparent'};
  color: ${({ theme }) => theme.forecast.defaultText};
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.1s ease;
  position: relative;
  z-index: 1;

  &:hover {
    background: ${({ theme }) => theme.forecast.primary}20;
  }

  &:first-of-type {
    border-radius: 0.5rem 0.5rem 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 0.5rem 0.5rem;
  }
`

// Shared loading state
export const LoadingState = styled.div`
  padding: 1rem;
  text-align: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-style: italic;
`

// Shared empty state
export const EmptyState = styled.div`
  padding: 1rem;
  text-align: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-style: italic;
`

// Shared avatar component for mentions, systems, etc.
export const TypeaheadAvatar = styled.div<{ $shape?: 'circle' | 'rounded' }>`
  width: 2rem;
  height: 2rem;
  border-radius: ${({ $shape = 'circle' }) =>
    $shape === 'circle' ? '50%' : '0.25rem'};
  background: ${({ theme }) => theme.forecast.primary}30;
  color: ${({ theme }) => theme.forecast.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
`

// Shared info container
export const TypeaheadInfo = styled.div`
  flex: 1;
  min-width: 0;
`

// Shared primary text
export const TypeaheadPrimaryText = styled.div`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// Shared secondary text
export const TypeaheadSecondaryText = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
